import { Box } from "@mantine/core";
import { useEffect, useMemo, useState } from "react";
import ButtonGroup from "../Shared/ButtonGroup";
import { useAppDispatch, useAppSelector } from "../../store/hooks";
import { setCDICount } from "../../store/providerReducer";
import CDIAlertsV2Table from "./CDIAlertsV2Table";
import {
  extractCDIAlertsV2,
  normalizeCDIAlertStatus,
} from "../../utils/cdi-alert-utils";
import PollingRefreshIndicator from "../Shared/PollingRefreshIndicator";
import {
  useCDIAlerts,
  useCDIAlertsV2,
  useUpdateCDIAlertStatusV2,
} from "../../hooks/queries";

const TYPE_FILTER_MAP: Record<string, string[]> = {
  "All Alerts": [],
  "Dx Alerts": ["DCC", "ACV"],
  "Lab/Imaging Alerts": ["AI", "AL"],
};

const CDI = () => {
  const dispatch = useAppDispatch();
  const mrn = useAppSelector((state) => state?.fhir?.mrn);

  const [activeTab, setActiveTab] = useState<string>("Pending");
  const [activeTypeFilter, setActiveTypeFilter] =
    useState<string>("All Alerts");

  const updateCDIAlert = useUpdateCDIAlertStatusV2();

  const v2FilterParams = useMemo(() => {
    const params: Record<string, string> = {};

    if (mrn?.trim()) {
      params.mrn_no = mrn.trim();
    }

    if (activeTab !== "All") {
      params.status_filter = activeTab.trim().toUpperCase();
    }

    const typeFilterTypes = TYPE_FILTER_MAP[activeTypeFilter];
    if (typeFilterTypes && typeFilterTypes.length === 1) {
      params.type_filter = typeFilterTypes[0];
      params.alert_type_filter = typeFilterTypes[0];
    }

    return params;
  }, [activeTab, activeTypeFilter, mrn]);

  const legacyParams = useMemo(
    () => ({
      mrn_no: mrn,
      status_filter: activeTab === "All" ? null : activeTab?.toUpperCase(),
    }),
    [activeTab, mrn]
  );

  const {
    data: cdiAlertsV2Response,
    isLoading: initialLoading,
    isFetching,
  } = useCDIAlertsV2(v2FilterParams, {
    enabled: Boolean(mrn),
    polling: true,
  });

  const { data: legacyResponse } = useCDIAlerts(legacyParams, Boolean(mrn));

  const v2Alerts = useMemo(() => {
    if (cdiAlertsV2Response?.status === 200) {
      return extractCDIAlertsV2(cdiAlertsV2Response.data);
    }
    return [];
  }, [cdiAlertsV2Response]);

  useEffect(() => {
    if (cdiAlertsV2Response?.status === 200) {
      dispatch(
        setCDICount(
          v2Alerts.filter(
            (alert) => normalizeCDIAlertStatus(alert.status) === "PENDING"
          ).length
        )
      );
    } else if (legacyResponse?.status === 200) {
      dispatch(
        setCDICount(legacyResponse?.data?.metadata?.statusCounts?.PENDING)
      );
    }
  }, [cdiAlertsV2Response, dispatch, legacyResponse, v2Alerts]);

  const isRefreshing = isFetching && !initialLoading;

  const handleAcknowledge = async (alertId: string) => {
    if (!mrn) {
      console.log("Missing MRN for CDI acknowledge");
      return;
    }

    try {
      await updateCDIAlert.mutateAsync({
        alert_id: alertId,
        mrn_no: mrn,
        new_status: "ACKNOWLEDGED",
      });
    } catch (ackError: any) {
      console.log("Error updating alert status");
    }
  };

  async function handleTabChange(tab: string) {
    setActiveTab(tab);
  }

  const filteredV2Alerts = useMemo(() => {
    let filtered = v2Alerts;

    if (activeTab !== "All") {
      const expectedStatus = activeTab.toUpperCase();
      filtered = filtered.filter(
        (alert) => normalizeCDIAlertStatus(alert.status) === expectedStatus
      );
    }

    const typeFilterTypes = TYPE_FILTER_MAP[activeTypeFilter];
    if (typeFilterTypes && typeFilterTypes.length > 0) {
      filtered = filtered.filter((alert) =>
        typeFilterTypes.includes(alert.type)
      );
    }

    const severityOrder: Record<string, number> = {
      HIGH: 0,
      MEDIUM: 1,
      LOW: 2,
    };

    filtered.sort(
      (a, b) =>
        (severityOrder[a.severity] ?? 3) - (severityOrder[b.severity] ?? 3)
    );

    return filtered;
  }, [activeTab, activeTypeFilter, v2Alerts]);

  return (
    <div style={{ height: "100%", display: "flex", flexDirection: "column" }}>
      <Box
        px={0}
        py={"xs"}
        style={{
          display: "flex",
          flexDirection: "row",
          alignItems: "center",
          justifyContent: "space-between",
          columnGap: "12px",
          position: "relative",
          zIndex: 600,
        }}
      >
        <ButtonGroup
          buttons={["All Alerts", "Dx Alerts", "Lab/Imaging Alerts"]}
          defaultActive='All Alerts'
          onChange={(tab: string) => setActiveTypeFilter(tab)}
          tooltips={{
            "Dx Alerts": "Diagnosis Capture Consideration, Alert for Clinical Validation",
            "Lab/Imaging Alerts": "Abnormal Lab, Abnormal Imaging",
          }}
        />
        <Box
          style={{
            display: "flex",
            alignItems: "center",
            gap: "10px",
            flexShrink: 0,
          }}
        >
          <PollingRefreshIndicator visible={isRefreshing} />
          <ButtonGroup
            buttons={["Pending", "Acknowledged", "All"]}
            defaultActive='Pending'
            onChange={handleTabChange}
          />
        </Box>
      </Box>

      <CDIAlertsV2Table
        alerts={filteredV2Alerts}
        loading={initialLoading}
        onAcknowledge={handleAcknowledge}
      />
    </div>
  );
};

export default CDI;
